// Instrumentation du diagnostic technique. Deux sources, délibérément
// complémentaires :
//  - ce module PATCHE ce qui n'a pas d'équivalent observable : `console.*` (il
//    n'existe aucune API d'observation de la console), et `fetch`/XHR pour le
//    statut HTTP et la méthode que Resource Timing n'expose pas partout ;
//  - `diagnostics-observers.ts` OBSERVE passivement le reste — toutes les
//    requêtes (images, scripts, polices), les signaux du navigateur et les
//    violations CSP — sans rien patcher chez le client.
// La FORME de la donnée et les helpers purs vivent dans `diagnostics.ts` ;
// ce module est la partie qui touche au DOM.
//
// Deux régimes, un seul code :
//  - ARMÉ (session Lab consentie, cf. `hasDiagnosticsParam`) : installé dès
//    `connectLeestoApp`, les rings couvrent toute la session de test et sont
//    joints à TOUT signalement — y compris un rapport texte seul.
//  - ÉPHÉMÈRE : créé par le recorder rrweb le temps d'un enregistrement quand
//    le diagnostic n'est pas armé, ce qui reproduit le comportement historique.
//
// Le sink (`setSink`) sert au recorder : pendant un enregistrement, les erreurs
// sont AUSSI émises en custom events rrweb pour rester entrelacées
// chronologiquement dans le replay.

import {
  CONSOLE_RING_TAG,
  CONSOLE_TAG,
  type ConsoleRingEntry,
  type DiagnosticsSnapshot,
  type ErrorRingEntry,
  isDiagnosticRunId,
  LEESTO_RUN_HEADER,
  NETWORK_RING_TAG,
  NETWORK_TAG,
} from "./diagnostics";
import { installDiagnosticObservers } from "./diagnostics-observers";
import { redactPII, safeUrl } from "./diagnostics-redact";
import { createRing, type NetworkEntry } from "./network-ring";

// Ré-export : la rédaction vit désormais dans `diagnostics-redact.ts` (partagée
// avec la source passive), mais elle reste exposée d'ici, son point d'entrée
// historique pour les appelants et les tests.
export { redactPII } from "./diagnostics-redact";

const NETWORK_RING_MAX = 50;
// Ring SÉPARÉ pour les ressources observées passivement : une page en charge
// des dizaines (images, scripts, polices), et mélangées au même ring elles
// évinceraient les appels `fetch`/XHR, qui portent le vrai statut HTTP et sont
// bien plus utiles à l'équipe.
const RESOURCE_RING_MAX = 30;
const CONSOLE_RING_MAX = 100;
const ERROR_RING_MAX = 50;
const MSG_CAP = 500;
const STACK_CAP = 8000;

/** Émetteur de custom events rrweb (branché par le recorder, sinon absent). */
export type EmitFn = <T>(tag: string, payload: T) => void;

export type DiagnosticsHandle = {
  /** Copie non destructive des trois rings. */
  snapshot: () => DiagnosticsSnapshot;
  /** Branche/débranche l'émission temps réel vers le recorder rrweb. */
  setSink: (emit: EmitFn | null) => void;
  /** Émet les rings log/info + réseau vers le sink courant, en un event chacun
   *  (trop bavards pour un event par ligne). À appeler AVANT `teardown`. */
  flushRings: () => void;
  /** Restauration idempotente de tout ce qui a été patché. */
  teardown: () => void;
};

/** Stack trace d'une valeur (Error uniquement), rédigée et bornée. La stack est
 *  souvent LA donnée manquante côté équipe : message seul = symptôme, stack =
 *  localisation. Best-effort : undefined si la valeur n'en porte pas. */
function stackOf(value: unknown): string | undefined {
  const stack = value instanceof Error ? value.stack : undefined;
  if (!stack) return undefined;
  return redactPII(stack).slice(0, STACK_CAP);
}

/** Représentation texte courte et sûre d'un argument loggé (sans dump massif). */
function stringifyArg(a: unknown): string {
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  if (typeof a === "string") return a;
  if (typeof a === "object" && a !== null) {
    try {
      return JSON.stringify(a, (key, value) => {
        if (
          /^(authorization|cookie|set-cookie|token|access_token|refresh_token|password|secret|api[-_]?key)$/i.test(
            key,
          )
        ) {
          return "[masqué]";
        }
        return value;
      }).slice(0, 500);
    } catch {
      return "[objet]";
    }
  }
  return String(a);
}

/** Taille de réponse dérivée UNIQUEMENT du header content-length (jamais du body). */
function sizeFromContentLength(v: string | null): number | undefined {
  if (!v) return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

const NOOP_HANDLE: DiagnosticsHandle = {
  snapshot: () => ({ errors: [], logs: [], network: [] }),
  setSink: () => {},
  flushRings: () => {},
  teardown: () => {},
};

/**
 * Installe la capture et retourne son handle. Sans `sink`, rien n'est émis :
 * la donnée s'accumule dans les rings et n'en sort qu'au `snapshot()` d'un
 * signalement.
 */
export function installDiagnostics(opts: {
  captureNetwork: boolean;
  runId?: string;
  /** Origines supplémentaires autorisées à recevoir X-Leesto-Run-Id. */
  runIdOrigins?: string[];
  sink?: EmitFn | null;
}): DiagnosticsHandle {
  if (typeof window === "undefined") return NOOP_HANDLE;

  const netRing = createRing<NetworkEntry>(NETWORK_RING_MAX);
  const resourceRing = createRing<NetworkEntry>(RESOURCE_RING_MAX);
  const consoleRing = createRing<ConsoleRingEntry>(CONSOLE_RING_MAX);
  const errorRing = createRing<ErrorRingEntry>(ERROR_RING_MAX);
  const runId = isDiagnosticRunId(opts.runId) ? opts.runId : undefined;
  const isRunIdAllowed = (raw: unknown): boolean => {
    if (!runId) return false;
    try {
      const target = new URL(String(raw), location.href);
      return target.origin === location.origin || (opts.runIdOrigins ?? []).includes(target.origin);
    } catch {
      return false;
    }
  };

  let sink: EmitFn | null = opts.sink ?? null;
  const emit: EmitFn = (tag, payload) => {
    sink?.(tag, payload);
  };

  const recordNet = (e: NetworkEntry) => {
    if (opts.captureNetwork) netRing.push(e);
  };

  /** Erreur : bufferisée dans le ring ET streamée au sink (entrelacement dans
   *  le replay). La stack du premier argument Error localise le bug. */
  const recordError = (
    level: "error" | "warn",
    message: string,
    stack?: string,
    details: Partial<ErrorRingEntry> = {},
  ) => {
    if (!message) return;
    const entry: ErrorRingEntry = {
      level,
      message,
      ...(stack ? { stack } : {}),
      ...details,
      at: Date.now(),
    };
    errorRing.push(entry);
    emit(CONSOLE_TAG, entry);
  };

  const logConsole = (level: "error" | "warn", args: unknown[]) => {
    try {
      const message = redactPII(args.map(stringifyArg).join(" ")).slice(0, MSG_CAP);
      const error = args.find((a) => a instanceof Error);
      recordError(level, message, stackOf(error), {
        mechanism: "console",
        ...(error instanceof Error && error.name ? { name: error.name } : {}),
      });
    } catch {
      // capture best-effort : ne jamais casser le code applicatif loggé.
    }
  };
  const recordLog = (level: "log" | "info", args: unknown[]) => {
    try {
      const message = redactPII(args.map(stringifyArg).join(" ")).slice(0, MSG_CAP);
      if (message) consoleRing.push({ level, message, at: Date.now() });
    } catch {
      // capture best-effort.
    }
  };

  const origError = console.error;
  const origWarn = console.warn;
  const origLog = console.log;
  const origInfo = console.info;
  console.error = (...args: unknown[]) => {
    logConsole("error", args);
    return origError.apply(console, args as never);
  };
  console.warn = (...args: unknown[]) => {
    logConsole("warn", args);
    return origWarn.apply(console, args as never);
  };
  console.log = (...args: unknown[]) => {
    recordLog("log", args);
    return origLog.apply(console, args as never);
  };
  console.info = (...args: unknown[]) => {
    recordLog("info", args);
    return origInfo.apply(console, args as never);
  };

  const onError = (e: Event) => {
    // Un échec de CHARGEMENT de ressource (<img>, <script>, <link>) est dispatché
    // sur l'élément et NE REMONTE PAS : seule la phase de capture le voit. D'où
    // le `true` ci-dessous — sans lui, un chunk JS en 404 nous échappait
    // totalement, alors que c'est un des bugs de test les plus fréquents.
    const target = e.target;
    if (target instanceof Element) {
      const src =
        (target as Partial<HTMLImageElement & HTMLScriptElement>).src ||
        (target as Partial<HTMLLinkElement>).href ||
        "";
      if (!src) return;
      recordError(
        "error",
        `Ressource non chargée : <${target.tagName.toLowerCase()}> ${safeUrl(src)}`.slice(
          0,
          MSG_CAP,
        ),
        undefined,
        { mechanism: "resource", sourceUrl: safeUrl(src) },
      );
      return;
    }
    const err = e as ErrorEvent;
    recordError(
      "error",
      redactPII(`Erreur non gérée : ${err.message}`).slice(0, MSG_CAP),
      stackOf(err.error),
      {
        mechanism: "window.error",
        ...(typeof err.error?.name === "string" ? { name: err.error.name } : {}),
        ...(typeof err.filename === "string" && err.filename
          ? { sourceUrl: safeUrl(err.filename) }
          : {}),
        ...(typeof err.lineno === "number" && err.lineno > 0 ? { line: err.lineno } : {}),
        ...(typeof err.colno === "number" && err.colno > 0 ? { column: err.colno } : {}),
      },
    );
  };
  const onRejection = (e: PromiseRejectionEvent) => {
    recordError(
      "error",
      redactPII(`Promesse rejetée : ${stringifyArg(e.reason)}`).slice(0, MSG_CAP),
      stackOf(e.reason),
      {
        mechanism: "unhandledrejection",
        ...(e.reason instanceof Error && e.reason.name ? { name: e.reason.name } : {}),
      },
    );
  };
  window.addEventListener("error", onError, true);
  window.addEventListener("unhandledrejection", onRejection);

  // --- fetch ---------------------------------------------------------------
  const origFetch = typeof window.fetch === "function" ? window.fetch.bind(window) : null;
  if (origFetch) {
    window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
      const start = Date.now();
      const method = (
        init?.method ??
        (input instanceof Request ? input.method : "GET") ??
        "GET"
      ).toUpperCase();
      const url = safeUrl(input);
      let requestInit = init;
      if (runId && isRunIdAllowed(input)) {
        const headers = new Headers(input instanceof Request ? input.headers : undefined);
        for (const [key, value] of new Headers(init?.headers)) headers.set(key, value);
        headers.set(LEESTO_RUN_HEADER, runId);
        requestInit = { ...init, headers };
      }
      try {
        const res = await origFetch(input, requestInit);
        // Capture best-effort : ne JAMAIS convertir un succès en échec ni jeter
        // l'erreur d'instrumentation à l'appelant. `res` est toujours renvoyé.
        try {
          const requestId = res.headers.get("x-request-id");
          const traceparent = res.headers.get("traceparent");
          recordNet({
            method,
            url,
            status: res.status,
            durationMs: Date.now() - start,
            responseSize: sizeFromContentLength(res.headers.get("content-length")),
            contentType: res.headers.get("content-type") ?? undefined,
            startedAt: start,
            ...(requestId ? { requestId: requestId.slice(0, 200) } : {}),
            ...(traceparent ? { traceparent: traceparent.slice(0, 200) } : {}),
            ...(runId ? { runId } : {}),
          });
          if (!res.ok) {
            emit(NETWORK_TAG, { method, url, status: res.status, durationMs: Date.now() - start });
          }
        } catch {
          // capture best-effort : ne jamais casser l'appel fetch applicatif.
        }
        return res;
      } catch (err) {
        // status 0 = la requête n'a pas abouti (réseau coupé, CORS, abort…).
        try {
          const durationMs = Date.now() - start;
          recordNet({
            method,
            url,
            status: 0,
            durationMs,
            startedAt: start,
            failed: true,
            ...(runId ? { runId } : {}),
          });
          emit(NETWORK_TAG, { method, url, status: 0, durationMs });
        } catch {
          // capture best-effort.
        }
        throw err;
      }
    }) as typeof window.fetch;
  }

  // --- XMLHttpRequest ------------------------------------------------------
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  type XhrMeta = { method: string; url: string; start: number; propagate: boolean };
  const META = "__echoNet" as const;
  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequest & { [META]?: XhrMeta },
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    this[META] = {
      method: (method || "GET").toUpperCase(),
      url: safeUrl(url),
      start: 0,
      propagate: isRunIdAllowed(url),
    };
    return origOpen.apply(this, [method, url, ...rest] as never);
  };
  XMLHttpRequest.prototype.send = function (
    this: XMLHttpRequest & { [META]?: XhrMeta },
    ...args: unknown[]
  ) {
    const meta = this[META];
    if (meta) {
      meta.start = Date.now();
      if (runId && meta.propagate) {
        try {
          this.setRequestHeader(LEESTO_RUN_HEADER, runId);
        } catch {
          // Header injection is best-effort; never break the XHR.
        }
      }
      // `{ once: true }` : sinon la réutilisation d'une même instance XHR (polling)
      // ferait re-tirer ce listener au 2e loadend → entrée corrompue.
      this.addEventListener(
        "loadend",
        () => {
          try {
            const durationMs = Date.now() - meta.start;
            const status = this.status;
            const failed = status === 0;
            const requestId = this.getResponseHeader("x-request-id");
            const traceparent = this.getResponseHeader("traceparent");
            recordNet({
              method: meta.method,
              url: meta.url,
              status,
              durationMs,
              responseSize: sizeFromContentLength(this.getResponseHeader("content-length")),
              contentType: this.getResponseHeader("content-type") ?? undefined,
              startedAt: meta.start,
              ...(failed ? { failed: true } : {}),
              ...(runId ? { runId } : {}),
              ...(requestId ? { requestId: requestId.slice(0, 200) } : {}),
              ...(traceparent ? { traceparent: traceparent.slice(0, 200) } : {}),
            });
            if (failed || status >= 400) {
              emit(NETWORK_TAG, { method: meta.method, url: meta.url, status, durationMs });
            }
          } catch {
            // capture best-effort : ne jamais casser la requête XHR applicative.
          }
        },
        { once: true },
      );
    }
    return origSend.apply(this, args as never);
  };

  // --- Socle passif --------------------------------------------------------
  // Couvre ce que le wrapper ne voit pas (images, scripts, polices, CSS) et
  // rejoue rétroactivement le démarrage via `buffered: true`.
  const observers = installDiagnosticObservers({
    onResource: (entry) => {
      if (opts.captureNetwork) resourceRing.push(runId ? { ...entry, runId } : entry);
    },
    onReport: (level, message) =>
      recordError(level, redactPII(message).slice(0, MSG_CAP), undefined, { mechanism: "report" }),
  });

  /** Les deux sources réseau fusionnées, dans l'ordre chronologique : le
   *  wrapper (statut HTTP exact) et l'observation passive (couverture large). */
  const mergedNetwork = (): NetworkEntry[] =>
    opts.captureNetwork
      ? [...netRing.snapshot(), ...resourceRing.snapshot()].sort(
          (a, b) => a.startedAt - b.startedAt,
        )
      : [];

  let restored = false;
  return {
    snapshot: () => ({
      errors: errorRing.snapshot(),
      logs: consoleRing.snapshot(),
      network: mergedNetwork(),
      ...(runId ? { runId } : {}),
    }),
    setSink: (next) => {
      sink = next;
    },
    flushRings: () => {
      // `snapshot()` est NON destructif : le flush d'une session ne doit jamais
      // amputer les rings permanents d'un rapport ultérieur.
      const entries = mergedNetwork();
      if (entries.length > 0) emit(NETWORK_RING_TAG, { entries });
      const logs = consoleRing.snapshot();
      if (logs.length > 0) emit(CONSOLE_RING_TAG, { entries: logs });
      const errors = errorRing.snapshot();
      if (errors.length > 0) emit(CONSOLE_TAG, { entries: errors });
    },
    teardown: () => {
      if (restored) return;
      restored = true;
      sink = null;
      observers.teardown();
      console.error = origError;
      console.warn = origWarn;
      console.log = origLog;
      console.info = origInfo;
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection);
      if (origFetch) window.fetch = origFetch;
      XMLHttpRequest.prototype.open = origOpen;
      XMLHttpRequest.prototype.send = origSend;
    },
  };
}
